import { reportHandledException } from '@/lib/error-reporting'
import {
  ACTIVE_CONTEXT_HEADER,
  ApiError,
  DEFAULT_ACTIVE_CONTEXT,
  getActiveContextHeaderValue,
  type ApiClient,
} from '@kiao-voice/api-client'
import { ACTIVE_CONTEXT_FORBIDDEN_ERROR, PLAN_CODES, type OrganizationKind } from '@kiao-voice/core'
import { createWebApiClient, getWebApiClient } from '../../lib/web-api-client'
import { sendXhrUpload, XhrUploadStalledError } from '../../lib/xhr-upload'
import { beginUploadActivity } from './upload-activity'
import {
  createUploadProgressLedger,
  type UploadProgressCallbacks,
  type UploadProgressSnapshot,
} from './upload-progress'
import type { AudioBatchDownloadFormat } from './download-formats'
import type {
  AudioBatchDeleteResult,
  AudioBatchDisplayStatus,
  AudioBatchItem,
  AudioBatchMeResponse,
  AudioBatchPreflightRequest,
  AudioBatchPreflightResponse,
  AudioBatchSegmentsResponse,
  AudioBatchSegmentStructureResponse,
  AudioBatchSourceUrlSubmitRequest,
  AudioBatchStagedSubmitRequest,
  AudioBatchStatusResponse,
  AudioBatchSubmitResponse,
  AudioBatchValidationError,
  AudioBatchVideoAbandonResponse,
  AudioTranscriptListResponse,
  TranscriptSegmentView,
  TranscriptSpeaker,
  TranscriptSuggestionDisposition,
  TranscriptSuggestionReviewResponse,
  VisualSegmentView,
} from './types'

export type { TranscriptSuggestionDisposition }

const PLANS: readonly AudioBatchMeResponse['plan'][] = PLAN_CODES

/**
 * Per-call options. The bearer token + `X-Active-Context` are injected by the
 * shared web client from storage (#223), and the stale-context self-heal (403
 * `active_context_forbidden` + 400 `invalid_active_context`) lives there too.
 * `fetchFn` is a test seam.
 */
interface RequestOptions {
  fetchFn?: typeof fetch
}

export interface SubmitOptions extends RequestOptions, UploadProgressCallbacks {
  /** Cancels browser transfer; chunked uploads are discarded server-side before rejection. */
  signal?: AbortSignal
  enhancementHints?: string[]
  visualContext?: boolean
  /** Upload language. `taigi` routes to Breeze ASR-26 (server coerces to `auto`
   * unless the operator flag is on); omit/`auto` uses default STT. */
  language?: string
  /** 整理範本 to apply once each transcript completes. Omitted = transcript only,
   *  which is the default: 整理 costs credits and must be asked for. */
  notesTemplateId?: number
  /** Share every uploaded item with the currently active team workspace. */
  shareToTeam?: boolean
}

export type { UploadFileState, UploadPhase, UploadProgressSnapshot } from './upload-progress'

/**
 * Total-size ceiling for the single-request multipart upload. Above this the
 * client switches to the chunked path (independent ~20 MB part requests, sent
 * browser-direct to R2 whenever the server hands back `partUrls`).
 *
 * Two edge constraints stack here, and the tighter one sets the value:
 *  - Cloudflare rejects request bodies above 100 MB outright.
 *  - The edge cancels a request whose origin response takes ~100 s, and a
 *    multipart POST cannot respond until the whole body has arrived — so a
 *    slow uplink turns any large single request into a guaranteed 500,
 *    however stable the connection is. 20 MB keeps the worst case inside
 *    that window down to ~1.6 Mbps uplinks.
 *
 * What chunking buys is a bound per request, not immunity: the direct leg
 * bypasses the edge entirely, while the proxy fallback still crosses it —
 * carrying one ≤20 MB part rather than the whole batch, so it inherits the
 * same ~1.6 Mbps floor instead of the batch-sized one.
 */
const SINGLE_REQUEST_UPLOAD_MAX_BYTES = 20 * 1024 * 1024
const CHUNK_UPLOAD_MAX_ATTEMPTS = 3
const CHUNK_UPLOAD_RETRY_BASE_MS = 250

export type AudioBatchUploadFailurePhase =
  | 'multipart'
  | 'initiate'
  | 'direct_part'
  | 'proxy_part'
  | 'complete'
  | 'finalize'

export interface AudioBatchUploadFailureContext {
  phase: AudioBatchUploadFailurePhase
  directFallback: boolean
}

const uploadFailureContexts = new WeakMap<object, AudioBatchUploadFailureContext>()

function markAudioBatchUploadFailure(
  error: unknown,
  context: AudioBatchUploadFailureContext
): unknown {
  if ((typeof error === 'object' && error !== null) || typeof error === 'function') {
    uploadFailureContexts.set(error, context)
  }
  return error
}

/** Read the PII-free transport context attached when a submit ultimately failed. */
export function getAudioBatchUploadFailureContext(
  error: unknown
): AudioBatchUploadFailureContext | null {
  if ((typeof error !== 'object' || error === null) && typeof error !== 'function') return null
  return uploadFailureContexts.get(error) ?? null
}

interface ChunkUploadTicket {
  key: string
  uploadId: string
  filename: string
  size: number
  mimeType: string
  /**
   * Presigned per-part R2 PUT URLs (partUrls[n] = part n+1). Present when the
   * server has direct upload enabled; chunk bytes then go straight to R2 and
   * only the tiny initiate/complete/store calls touch the API origin. Absent
   * (older API or kill switch off) → the proxy `uploads/part` path.
   */
  partUrls?: string[]
}

/**
 * A direct-mode chunk PUT failed in a way that will fail identically on a
 * retry (rejected status, unusable response, stalled transfer). Deliberately
 * NOT a TypeError: `isRetryableChunkUploadError` must not resend the same
 * 20 MB chunk at a deterministic failure — the caller demotes the whole
 * operation to the proxy path on the first occurrence instead.
 */
class DirectPutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'DirectPutError'
  }
}

/**
 * PUT one chunk straight to R2 via its presigned URL, with the same stall
 * watchdog/cancel guards as every other browser upload path. Returns the
 * part's ETag (exposed through the bucket CORS config) for the complete
 * call. Throws `DirectPutError` when the transfer stalls, the response is
 * not OK, or it carries no usable ETag — the caller then demotes to the
 * proxy part upload for the rest of the operation. A test-seam `fetchFn`
 * bypasses the XHR plumbing entirely.
 */
async function putChunkDirect(
  url: string,
  chunk: Blob,
  opts: {
    fetchFn?: typeof fetch
    signal?: AbortSignal
    /** This part's own 0..1. Only the XHR path can report it; `fetchFn` cannot. */
    onProgress?: (fraction: number) => void
  }
): Promise<string> {
  let response: Response
  try {
    if (opts.fetchFn) {
      response = await opts.fetchFn(url, { method: 'PUT', body: chunk, signal: opts.signal })
    } else {
      response = await new Promise<Response>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', url)
        sendXhrUpload(xhr, chunk, {
          signal: opts.signal,
          resolve,
          reject,
          ...(opts.onProgress ? { onUploadProgress: opts.onProgress } : {}),
        })
      })
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    // Stalls and transport failures propagate as-is: they are transient, and
    // the caller's retry wrapper gives the SAME presigned URL another chance
    // before demoting the whole operation to the proxy path.
    throw error
  }
  if (!response.ok) {
    throw new DirectPutError(`Direct chunk PUT failed (HTTP ${response.status})`)
  }
  const etag = response.headers.get('ETag') ?? response.headers.get('etag')
  if (!etag) {
    // CORS is missing ExposeHeaders: ETag — direct mode cannot complete the
    // multipart upload without it, so treat as a hard direct-mode failure.
    throw new DirectPutError('Direct chunk PUT returned no ETag')
  }
  return etag
}

function isRetryableChunkUploadError(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 408 || error.status === 429 || error.status >= 500
  }
  // A stalled direct PUT is transient by definition — retry the same part.
  if (error instanceof XhrUploadStalledError) return true
  // Browser fetch rejects network failures with TypeError. Do not retry aborts:
  // they represent an explicit cancellation rather than a transient transport
  // failure and should move straight to server-side multipart cleanup.
  return error instanceof TypeError
}

async function retryChunkRequest<T>(request: () => Promise<T>): Promise<T> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await request()
    } catch (error) {
      if (attempt >= CHUNK_UPLOAD_MAX_ATTEMPTS || !isRetryableChunkUploadError(error)) {
        throw error
      }
      reportHandledException(error, { source: 'features/batch-transcription/api-client.ts' })
      await new Promise((resolve) =>
        setTimeout(resolve, CHUNK_UPLOAD_RETRY_BASE_MS * 2 ** (attempt - 1))
      )
    }
  }
}

/**
 * Distinguishes "the call hit the network and the server said 4xx/5xx"
 * from "the call never made it" or "the call returned malformed JSON".
 * Lets the UI surface auth-vs-validation-vs-credit-vs-server-error with
 * different remediations (CTA to /pricing for 402, sign-out for 401,
 * etc.).
 */
export interface AudioBatchApiErrorInit {
  message: string
  status: number
  validationErrors?: AudioBatchValidationError[] | null
  /**
   * Structured error code from the API body, e.g. `insufficient_credits`
   * for a 402. Null when the body is a generic "{ errors: [...] }"
   * validation envelope or unparseable garbage.
   */
  code?: string | null
  /**
   * Caller's current credit balance, surfaced by the 402 body so the
   * UI can show "You have N credits remaining". Null for non-402.
   */
  balance?: number | null
}

export class AudioBatchApiError extends Error {
  readonly status: number
  readonly validationErrors: AudioBatchValidationError[] | null
  readonly code: string | null
  readonly balance: number | null

  constructor(init: AudioBatchApiErrorInit) {
    super(init.message)
    this.name = 'AudioBatchApiError'
    this.status = init.status
    this.validationErrors = init.validationErrors ?? null
    this.code = init.code ?? null
    this.balance = init.balance ?? null
  }
}

/**
 * True for a stale/invalid active-context rejection — a transient,
 * self-recovering condition (`recoverFromStaleActiveContext` already cleared
 * the context, so the next request omits the dead header), NOT an auth failure
 * to sign out on. Covers both reject shapes so bootstrap AND mid-flight
 * (poll / submit / download) handling stay in lockstep:
 *  - 403 `active_context_forbidden` — context resolves but access was lost.
 *  - 400 `invalid_active_context` — the stored value is malformed.
 * The page branches on this before its 401/403 → sign-out handling.
 */
export function isRecoverableActiveContextError(error: unknown): boolean {
  if (!(error instanceof AudioBatchApiError)) return false
  return (
    (error.status === 403 && error.code === ACTIVE_CONTEXT_FORBIDDEN_ERROR) ||
    (error.status === 400 && error.code === 'invalid_active_context')
  )
}

function client(opts?: RequestOptions): ApiClient {
  return opts?.fetchFn ? createWebApiClient(opts.fetchFn) : getWebApiClient()
}

/**
 * Map a thrown `ApiError` from the shared client to `AudioBatchApiError`,
 * preserving the status + body-derived `code` / `validationErrors` / `balance`
 * the transcription pages branch on. `preferServerMessage` surfaces the body's
 * `message` (submit's tailored insufficient-credits text) — off elsewhere so
 * reads keep their fixed fallback copy. Non-ApiError values propagate.
 */
/**
 * Map an `ApiError` onto the richer {@link AudioBatchApiError} the UI switches
 * on (402 credit, 403 stale context, 422 validation).
 *
 * Exported so the meeting-notes client maps errors IDENTICALLY rather than
 * growing its own near-copy — the active-context self-heal check
 * (`isRecoverableActiveContextError`) only works on errors shaped by this.
 */
export function mapBatchError(
  error: unknown,
  fallback: (status: number) => string,
  preferServerMessage = false
): unknown {
  if (!(error instanceof ApiError)) return error
  const body =
    error.body && typeof error.body === 'object' ? (error.body as Record<string, unknown>) : {}
  const code = typeof body.error === 'string' ? body.error : null
  const serverMessage = typeof body.message === 'string' ? body.message : null
  const validationErrors = Array.isArray(body.errors)
    ? (body.errors as AudioBatchValidationError[])
    : null
  const balance = typeof body.balance === 'number' ? body.balance : null
  return new AudioBatchApiError({
    message: preferServerMessage && serverMessage ? serverMessage : fallback(error.status),
    status: error.status,
    validationErrors,
    code,
    balance,
  })
}

/**
 * Confirms the stored bearer token still belongs to a KIAO internal-org
 * member (P4c: staff = active membership, not a plan value). The batch and
 * transcriptions pages run this against the stored token before anything
 * else, so a non-staff account gets a clear "no access" error instead of a
 * silent redirect that 401/403s on the first real request.
 *
 * - 200 → caller has internal access; returns the email for the header
 * - 401 → token invalid/expired (the global `auth_middleware` rejected it)
 * - 403 → token valid but not a KIAO-org member (`internalAccess` rejected it)
 */
export async function probeInternalAccess(opts: RequestOptions = {}): Promise<{
  ok: true
  email: string
  plan: AudioBatchMeResponse['plan']
  maxFileMinutes: number | null
  maxFileBytes: number | null
  taigiEnabled: boolean
}> {
  let body: AudioBatchMeResponse
  try {
    body = await client(opts).request<AudioBatchMeResponse>('/api/v1/audio-batches/me', {
      method: 'GET',
    })
  } catch (error) {
    throw mapBatchError(error, (status) =>
      status === 401
        ? 'Your session has expired. Please sign in again.'
        : status === 403
          ? "Your account doesn't have access to the batch transcription tool."
          : `Auth probe failed (HTTP ${status}).`
    )
  }

  if (typeof body.email !== 'string') {
    // Don't shoehorn this into AudioBatchApiError — callers branch on
    // `error.status === 401 | 403` and a malformed-payload error
    // pretending to be `status: 200` would silently fall through any
    // such guard. A plain Error makes the unexpected case unmistakable.
    throw new Error('Auth probe returned an unexpected payload.')
  }
  return {
    ok: true,
    email: body.email,
    // Whitelist-guard keeps the contract's closed plan union honest at runtime:
    // an unknown/legacy plan string degrades to 'free' instead of leaking a
    // value the type swears cannot exist. (Defensive parse.)
    plan: PLANS.includes(body.plan) ? body.plan : 'free',
    // null = no per-file cap on this plan (internal users, pro tier if uncapped).
    maxFileMinutes: typeof body.maxFileMinutes === 'number' ? body.maxFileMinutes : null,
    // Defensive parse: the contract pins `maxFileBytes: number`, but this probe
    // still answers for an API that predates the field.
    maxFileBytes: typeof body.maxFileBytes === 'number' ? body.maxFileBytes : null,
    // Off unless the server flag is on — gates the 「國語+台語」 upload option.
    taigiEnabled: body.taigiEnabled === true,
  }
}

export async function submitAudioBatch(
  files: File[],
  opts: SubmitOptions = {}
): Promise<AudioBatchSubmitResponse> {
  const finishUpload = beginUploadActivity()
  try {
    if (files.reduce((total, file) => total + file.size, 0) > SINGLE_REQUEST_UPLOAD_MAX_BYTES) {
      return await submitChunkedAudioBatch(files, opts)
    }

    // The small-file path is one multipart POST through `client().upload`, which
    // has no progress hook. It still reports its PHASES, because a small upload
    // can still take real time on a weak uplink and silence is the thing being
    // fixed. Byte-level progress here would mean rebuilding that call on XHR —
    // deliberately not done: it is shared transport used by many endpoints, and
    // a ≤20 MB body is short enough that phase feedback carries it.
    opts.onPhase?.('preparing')
    const formData = new FormData()
    for (const file of files) {
      formData.append('files', file, file.name)
    }
    if (opts.enhancementHints && opts.enhancementHints.length > 0) {
      formData.append('enhancementHints', JSON.stringify(opts.enhancementHints))
    }
    if (opts.language && opts.language !== 'auto') {
      formData.append('language', opts.language)
    }
    if (opts.visualContext) {
      formData.append('visualContext', 'true')
    }
    if (opts.notesTemplateId) {
      formData.append('notesTemplateId', String(opts.notesTemplateId))
    }
    if (opts.shareToTeam) {
      formData.append('shareToTeam', 'true')
    }

    try {
      opts.onPhase?.('uploading')
      for (let index = 0; index < files.length; index++) opts.onFileState?.(index, 'uploading')
      const submitted = await client(opts).upload<AudioBatchSubmitResponse>(
        '/api/v1/audio-batches',
        formData,
        {
          signal: opts.signal,
        }
      )
      for (let index = 0; index < files.length; index++) opts.onFileState?.(index, 'done')
      opts.onPhase?.('finalizing')
      return submitted
    } catch (error) {
      // preferServerMessage: the require_credits middleware returns
      //   { error: 'insufficient_credits', message: '...', balance: 0 }
      // — surface that tailored copy + balance (mapBatchError extracts both) so
      // the page renders a CTA block instead of the generic "Upload failed".
      throw markAudioBatchUploadFailure(
        mapBatchError(error, (status) => `Upload failed (HTTP ${status}).`, true),
        { phase: 'multipart', directFallback: false }
      )
    }
  } finally {
    finishUpload()
  }
}

/**
 * Link mode of the upload dialog: media links (YouTube, a podcast episode, a
 * direct file URL) the worker downloads, instead of files sent from here. One
 * JSON request with no bytes, so there is no upload phase to report; the
 * dialog goes straight to its finishing state while the server creates the
 * items. The on-screen option never rides along: a link is fetched audio-only.
 *
 * An API that predates link mode ignores `sourceUrls` and answers 422
 * `no_files`; the dialog turns that into its own message.
 */
export async function submitAudioBatchLinks(
  sourceUrls: string[],
  opts: Omit<SubmitOptions, 'visualContext'> = {}
): Promise<AudioBatchSubmitResponse> {
  const payload: AudioBatchSourceUrlSubmitRequest = {
    sourceUrls,
    ...(opts.enhancementHints?.length
      ? { enhancementHints: JSON.stringify(opts.enhancementHints) }
      : {}),
    ...(opts.language && opts.language !== 'auto' ? { language: opts.language } : {}),
    ...(opts.notesTemplateId ? { notesTemplateId: opts.notesTemplateId } : {}),
    // String, not boolean: the API reads it through `parseBooleanParam`, like
    // the staged-upload JSON submit.
    ...(opts.shareToTeam ? { shareToTeam: 'true' } : {}),
  }
  opts.onPhase?.('finalizing')
  try {
    const submitted = await client(opts).request<AudioBatchSubmitResponse>(
      '/api/v1/audio-batches',
      { method: 'POST', signal: opts.signal, body: JSON.stringify(payload) }
    )
    for (let index = 0; index < sourceUrls.length; index++) opts.onFileState?.(index, 'done')
    return submitted
  } catch (error) {
    throw mapBatchError(error, (status) => `Submit failed (HTTP ${status}).`, true)
  }
}

async function submitChunkedAudioBatch(
  files: File[],
  opts: SubmitOptions
): Promise<AudioBatchSubmitResponse> {
  const api = client(opts)
  // A multipart operation belongs to the workspace selected when initiate
  // starts. Every API leg carries this explicit value so a context change from
  // another tab or caller cannot split one operation across workspaces.
  const pinnedActiveContext = getActiveContextHeaderValue() ?? DEFAULT_ACTIVE_CONTEXT
  const operationHeaders = (extra: Record<string, string> = {}): Record<string, string> => ({
    ...extra,
    [ACTIVE_CONTEXT_HEADER]: pinnedActiveContext,
  })
  // Generated before initiate so a lost initiate response can still be retried
  // or aborted by operation ID. The server persists the authoritative tickets.
  const operationId = crypto.randomUUID()
  opts.onPhase?.('preparing')
  let initiated: {
    operationId: string
    chunkSizeBytes: number
    uploads: ChunkUploadTicket[]
  } | null = null
  let failurePhase: AudioBatchUploadFailurePhase = 'initiate'
  let directFallback = false
  try {
    initiated = await retryChunkRequest(() =>
      api.request<{
        operationId: string
        chunkSizeBytes: number
        uploads: ChunkUploadTicket[]
      }>('/api/v1/audio-batches/uploads/initiate', {
        method: 'POST',
        headers: operationHeaders(),
        signal: opts.signal,
        body: JSON.stringify({
          operationId,
          files: files.map((file) => ({
            filename: file.name,
            size: file.size,
            mimeType: file.type || null,
          })),
          ...(opts.visualContext ? { visualContext: true } : {}),
        }),
      })
    )
    if (initiated.operationId !== operationId) {
      throw new Error('Chunk upload operation ID mismatch')
    }

    // Chunk size is only known once initiate answers, so the ledger is built
    // here rather than up front.
    const ledger = createUploadProgressLedger(
      files.map((file) => file.size),
      initiated.chunkSizeBytes
    )
    const report = (snapshot: UploadProgressSnapshot) => opts.onUploadProgress?.(snapshot)
    opts.onPhase?.('uploading')
    report(ledger.snapshot())

    // Direct mode: chunk bytes go straight to R2 via presigned URLs. Any
    // direct failure (CORS, expired URL, blocked host) demotes the WHOLE
    // operation to the proxy path — R2 keeps the last write per part number,
    // so re-uploading an already-direct-uploaded part through the proxy is
    // safe.
    let directMode = initiated.uploads.every(
      (ticket) => Array.isArray(ticket.partUrls) && ticket.partUrls.length > 0
    )

    for (const [fileIndex, ticket] of initiated.uploads.entries()) {
      const file = files[fileIndex]
      opts.onFileState?.(fileIndex, 'uploading')
      const parts: Array<{ partNumber: number; etag: string }> = []
      for (let offset = 0, partNumber = 1; offset < file.size; partNumber++) {
        const end = Math.min(offset + initiated.chunkSizeBytes, file.size)
        const chunk = file.slice(offset, end)
        let etag: string | null = null
        if (directMode) {
          failurePhase = 'direct_part'
          const partUrl = ticket.partUrls?.[partNumber - 1]
          if (partUrl) {
            try {
              // Transient failures (stall, network drop) retry the same part;
              // deterministic ones (rejected status, missing ETag →
              // DirectPutError) skip the retry entirely. Either way, once the
              // direct leg gives up, the whole operation demotes to the proxy
              // path, which carries its own retry policy.
              etag = await retryChunkRequest(() =>
                putChunkDirect(partUrl, chunk, {
                  ...opts,
                  onProgress: (fraction) =>
                    report(ledger.onPartProgress(fileIndex, partNumber, fraction)),
                })
              )
            } catch (error) {
              if (error instanceof DOMException && error.name === 'AbortError') throw error
              reportHandledException(error, {
                source: 'features/batch-transcription/api-client.ts',
              })
              directFallback = true
              directMode = false
              // The bytes are going again through the proxy. Say so plainly and
              // without alarm: nothing has failed, it is just slower.
              opts.onFileState?.(fileIndex, 'retrying')
            }
          } else {
            directMode = false
          }
        }
        if (etag === null) {
          failurePhase = 'proxy_part'
          const uploaded = await retryChunkRequest(() =>
            api.request<{ etag: string }>('/api/v1/audio-batches/uploads/part', {
              method: 'PUT',
              headers: {
                ...operationHeaders({
                  'Content-Type': 'application/octet-stream',
                  'X-Kiao-Upload-Key': ticket.key,
                  'X-Kiao-Upload-Id': ticket.uploadId,
                  'X-Kiao-Part-Number': String(partNumber),
                }),
              },
              body: chunk,
              signal: opts.signal,
            })
          )
          etag = uploaded.etag
          // The proxy leg carries no progress events, so the part only counts
          // when it lands. It reports on completion below either way.
          opts.onFileState?.(fileIndex, 'uploading')
        }
        parts.push({ partNumber, etag })
        report(ledger.onPartComplete(fileIndex, partNumber))
        offset = end
      }
      failurePhase = 'complete'
      await retryChunkRequest(() =>
        api.request('/api/v1/audio-batches/uploads/complete', {
          method: 'POST',
          headers: operationHeaders(),
          signal: opts.signal,
          body: JSON.stringify({
            key: ticket.key,
            uploadId: ticket.uploadId,
            expectedSize: file.size,
            parts,
          }),
        })
      )
      report(ledger.onFileComplete(fileIndex))
      opts.onFileState?.(fileIndex, 'done')
    }

    // Every byte is up; what remains is the server committing the batch. There
    // is no percentage for that, and the copy has to take over — a number that
    // simply disappears reads as something going wrong.
    opts.onPhase?.('finalizing')
    failurePhase = 'finalize'

    // `operationId` makes store idempotent. Retrying an uncertain response is
    // safer than deleting the staged object: the first request may already have
    // committed the batch and queued workers before its response was lost.
    const body: AudioBatchStagedSubmitRequest = {
      operationId,
      stagedFiles: initiated!.uploads.map((ticket) => ({
        key: ticket.key,
        filename: ticket.filename,
        size: ticket.size,
        mimeType: ticket.mimeType,
      })),
      ...(opts.enhancementHints?.length
        ? { enhancementHints: JSON.stringify(opts.enhancementHints) }
        : {}),
      ...(opts.language && opts.language !== 'auto' ? { language: opts.language } : {}),
      ...(opts.visualContext ? { visualContext: true } : {}),
      ...(opts.notesTemplateId ? { notesTemplateId: opts.notesTemplateId } : {}),
      // The one flag the API reads through the string-only
      // `parseBooleanParam`: a JSON `true` here comes back as
      // `invalid_share_to_team` and kills the whole batch. Mirror the
      // multipart encoding, the same way `enhancementHints` ships as a
      // JSON string on this path.
      ...(opts.shareToTeam ? { shareToTeam: 'true' } : {}),
    }
    return await retryChunkRequest(() =>
      api.request<AudioBatchSubmitResponse>('/api/v1/audio-batches', {
        method: 'POST',
        headers: operationHeaders(),
        signal: opts.signal,
        body: JSON.stringify(body),
      })
    )
  } catch (error) {
    // Always attempt operation-aware cleanup, including uncertain store
    // responses and lost initiate responses. The server atomically refuses to
    // delete once this operation has been claimed by a batch.
    await retryChunkRequest(() =>
      api.request('/api/v1/audio-batches/uploads/abort', {
        method: 'POST',
        headers: operationHeaders(),
        body: JSON.stringify({ operationId }),
      })
    ).catch((caughtError) => {
      reportHandledException(caughtError, {
        source: 'features/batch-transcription/api-client.ts',
      })
      return undefined
    })
    throw markAudioBatchUploadFailure(
      mapBatchError(error, (status) => `Upload failed (HTTP ${status}).`, true),
      { phase: failurePhase, directFallback }
    )
  }
}

export async function fetchAudioBatchStatus(
  batchId: number,
  opts: RequestOptions = {}
): Promise<AudioBatchStatusResponse> {
  try {
    return await client(opts).request<AudioBatchStatusResponse>(
      `/api/v1/audio-batches/${batchId}`,
      { method: 'GET' }
    )
  } catch (error) {
    throw mapBatchError(error, (status) => `Status fetch failed (HTTP ${status}).`)
  }
}

export async function fetchAudioItemTranscript(
  batchId: number,
  itemId: number,
  format: AudioBatchDownloadFormat,
  opts: RequestOptions,
  /** Whether the downloaded transcript should include speaker labels. OFF by
   *  default — exports carry no speaker info unless the user opts in via the
   *  download "include speakers" toggle. Sent explicitly either way; the server
   *  still suppresses labels for single-speaker audio. */
  includeSpeakerLabels: boolean = false
): Promise<string> {
  const path = `/api/v1/audio-batches/${batchId}/items/${itemId}/transcript?format=${encodeURIComponent(format)}&speakerLabels=${includeSpeakerLabels}`
  try {
    return await client(opts).requestText(path, { method: 'GET' })
  } catch (error) {
    throw mapBatchError(error, (status) => `Transcript download failed (HTTP ${status}).`)
  }
}

// ---------------------------------------------------------------------------
// Transcript history + speaker-editor endpoints
//
// All sit under the same `/audio-batches` group, share the bearer auth and
// `AudioBatchApiError` mapping, and either read JSON or send a JSON body. The
// `requestJson` helper centralises method/headers/body/error handling so each
// of the eight wrappers below is a one-liner that just names its route shape.
// ---------------------------------------------------------------------------

/**
 * Issue a JSON request and parse the JSON response. A `body` of `undefined`
 * sends no request body (GET/DELETE); any other value — including `null` — is
 * JSON-encoded with a `content-type: application/json` header. Non-2xx maps to
 * an `AudioBatchApiError` carrying the status so callers can branch on
 * 401/403 (re-auth) vs 404 (gone) vs the rest.
 */
async function requestJson<T>(
  path: string,
  method: string,
  opts: RequestOptions,
  body?: unknown
): Promise<T> {
  // The shared client injects auth + X-Active-Context and self-heals a stale
  // context (403 active_context_forbidden / 400 invalid_active_context) before
  // throwing, so `isRecoverableActiveContextError` stays true on the mapped
  // error and the page's recoverable-context retry fires.
  try {
    return await client(opts).request<T>(`/api/v1${path}`, {
      method,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch (error) {
    throw mapBatchError(error, (status) => `Request failed (HTTP ${status}).`)
  }
}

interface ListAudioBatchesOptions extends RequestOptions {
  page?: number
  perPage?: number
  /** Case-insensitive filename substring filter (server-side). */
  q?: string
  /** Display-status filter; omit or 'all' for no filter. */
  status?: AudioBatchDisplayStatus | 'all'
  /** Personal upload history or the active team's shared workspace. */
  scope?: OrganizationKind
}

/**
 * List the caller's transcriptions, newest first — FLATTENED to one row per file
 * (item). Supports server-side filename search (`q`) + status filter + paging.
 */
export async function listAudioBatches(
  opts: ListAudioBatchesOptions = {}
): Promise<AudioTranscriptListResponse> {
  const query = new URLSearchParams()
  if (opts.page !== undefined) query.set('page', String(opts.page))
  if (opts.perPage !== undefined) query.set('perPage', String(opts.perPage))
  if (opts.q) query.set('q', opts.q)
  if (opts.status && opts.status !== 'all') query.set('status', opts.status)
  if (opts.scope === 'team') query.set('scope', 'team')
  const suffix = query.toString() ? `?${query.toString()}` : ''
  return requestJson<AudioTranscriptListResponse>(`/audio-batches${suffix}`, 'GET', opts)
}

export async function shareAudioItemWithTeam(
  batchId: number,
  itemId: number,
  organizationId: number,
  opts: RequestOptions = {}
): Promise<{ item: AudioBatchItem }> {
  return requestJson<{ item: AudioBatchItem }>(
    `/audio-batches/${batchId}/items/${itemId}/share`,
    'POST',
    opts,
    { organizationId }
  )
}

export async function unshareAudioItemFromTeam(
  batchId: number,
  itemId: number,
  opts: RequestOptions = {}
): Promise<{ item: AudioBatchItem }> {
  return requestJson<{ item: AudioBatchItem }>(
    `/audio-batches/${batchId}/items/${itemId}/share`,
    'DELETE',
    opts
  )
}

/** Structured transcript (segments + roster + playback URL) for one item. */
export async function fetchAudioBatchSegments(
  batchId: number,
  itemId: number,
  opts: RequestOptions = {}
): Promise<AudioBatchSegmentsResponse> {
  return requestJson<AudioBatchSegmentsResponse>(
    `/audio-batches/${batchId}/items/${itemId}/segments`,
    'GET',
    opts
  )
}

/**
 * On-demand higher-resolution waveform peaks for one item — the dock requests a
 * finer envelope as the user zooms past the low-res precomputed one. The server
 * decodes the audio (server-side, no CORS) and returns ONLY the peaks (KB), never
 * the audio. `resolution` is the desired bucket count (server clamps it).
 */
export async function fetchAudioItemWaveformPeaks(
  batchId: number,
  itemId: number,
  resolution: number,
  opts: RequestOptions = {}
): Promise<number[]> {
  const res = await requestJson<{ peaks: number[] }>(
    `/audio-batches/${batchId}/items/${itemId}/waveform-peaks?resolution=${Math.round(resolution)}`,
    'GET',
    opts
  )
  return res.peaks
}

/** Rename a roster speaker. `null` clears the name back to the default label. */
export async function renameSpeaker(
  batchId: number,
  itemId: number,
  code: string,
  displayName: string | null,
  opts: RequestOptions = {}
): Promise<TranscriptSpeaker> {
  return requestJson<TranscriptSpeaker>(
    `/audio-batches/${batchId}/items/${itemId}/speakers/${encodeURIComponent(code)}`,
    'PATCH',
    opts,
    { displayName }
  )
}

/** Rename a file — overwrites the item's display title. Returns the updated item. */
export async function renameAudioItem(
  batchId: number,
  itemId: number,
  filename: string,
  opts: RequestOptions = {}
): Promise<AudioBatchItem> {
  return requestJson<AudioBatchItem>(`/audio-batches/${batchId}/items/${itemId}`, 'PATCH', opts, {
    filename,
  })
}

/** Add a speaker the model didn't detect; the API assigns the next code. */
export async function addSpeaker(
  batchId: number,
  itemId: number,
  displayName: string,
  opts: RequestOptions = {}
): Promise<TranscriptSpeaker> {
  return requestJson<TranscriptSpeaker>(
    `/audio-batches/${batchId}/items/${itemId}/speakers`,
    'POST',
    opts,
    { displayName }
  )
}

/** Fields the unified segment PATCH accepts; any subset, at least one. */
export interface SegmentPatch {
  speaker?: string
  text?: string
  start?: number
  end?: number
}

/** Update one segment (by DB id): reassign speaker, edit text, and/or nudge time. */
export async function updateSegment(
  batchId: number,
  itemId: number,
  segmentId: number,
  patch: SegmentPatch,
  opts: RequestOptions = {}
): Promise<TranscriptSegmentView> {
  return requestJson<TranscriptSegmentView>(
    `/audio-batches/${batchId}/items/${itemId}/segments/${segmentId}`,
    'PATCH',
    opts,
    patch
  )
}

/** Update one on-screen visual segment's verbatim multiline Markdown. */
export async function updateVisualSegment(
  batchId: number,
  itemId: number,
  visualSegmentId: number,
  contentMd: string,
  opts: RequestOptions = {}
): Promise<VisualSegmentView> {
  return requestJson<VisualSegmentView>(
    `/audio-batches/${batchId}/items/${itemId}/visual-segments/${visualSegmentId}`,
    'PATCH',
    opts,
    { contentMd }
  )
}

export type TranscriptSuggestionDecision =
  | { status: 'rejected' }
  | {
      status: 'accepted'
      segmentId: number
      /** Last cue text the reviewer saw; the API rejects a stale overwrite. */
      expectedText: string
    }

/**
 * Wire type now lives in `@kiao-voice/api-contract/batch-transcription`
 * (`TranscriptSuggestionReviewResponse`); this named alias keeps existing
 * imports alive.
 */
export type TranscriptSuggestionReviewResult = TranscriptSuggestionReviewResponse

/** Persist a human review decision so the suggestion does not return on reload. */
export async function reviewTranscriptSuggestion(
  batchId: number,
  itemId: number,
  suggestionId: number,
  decision: TranscriptSuggestionDecision,
  opts: RequestOptions = {}
): Promise<TranscriptSuggestionReviewResult> {
  return requestJson<TranscriptSuggestionReviewResult>(
    `/audio-batches/${batchId}/items/${itemId}/suggestions/${suggestionId}`,
    'PATCH',
    opts,
    decision
  )
}

/**
 * Result of a structural segment edit (split/merge): the item's FULL updated
 * segment list (re-indexed) + `focusId`, the segment the editor should select
 * after (the new right half on split, the kept segment on merge).
 */
/** Named alias of the contract's `AudioBatchSegmentStructureResponse`. */
export type SegmentStructureResult = AudioBatchSegmentStructureResponse

/** Split one segment into two at `atIndex` within `text` (the current full text). */
export async function splitSegment(
  batchId: number,
  itemId: number,
  segmentId: number,
  text: string,
  atIndex: number,
  expectedText: string,
  opts: RequestOptions = {}
): Promise<SegmentStructureResult> {
  return requestJson<SegmentStructureResult>(
    `/audio-batches/${batchId}/items/${itemId}/segments/${segmentId}/split`,
    'POST',
    opts,
    { text, atIndex, expectedText }
  )
}

/** Which neighbour a segment merge targets. */
export type MergeDirection = 'prev' | 'next'

/** Merge one segment with its previous/next neighbour. */
export async function mergeSegment(
  batchId: number,
  itemId: number,
  segmentId: number,
  direction: MergeDirection,
  expectedAdjacentId: number,
  opts: RequestOptions = {}
): Promise<SegmentStructureResult> {
  return requestJson<SegmentStructureResult>(
    `/audio-batches/${batchId}/items/${itemId}/segments/${segmentId}/merge`,
    'POST',
    opts,
    { direction, expectedAdjacentId }
  )
}

/** Merge speaker `from` into `into` (reassigns its segments, then drops it). */
export async function mergeSpeakers(
  batchId: number,
  itemId: number,
  from: string,
  into: string,
  opts: RequestOptions = {}
): Promise<{ merged: { from: string; into: string } }> {
  return requestJson<{ merged: { from: string; into: string } }>(
    `/audio-batches/${batchId}/items/${itemId}/speakers/merge`,
    'POST',
    opts,
    { from, into }
  )
}

/** Named aliases of the contract's `AudioBatchDeleteResult` union. */
export type DeleteAudioBatchResult = AudioBatchDeleteResult
export type DeleteAudioItemResult = AudioBatchDeleteResult

/** Delete a whole batch, or cancel it when workers are still processing rows. */
export async function deleteAudioBatch(
  batchId: number,
  opts: RequestOptions = {}
): Promise<DeleteAudioBatchResult> {
  return requestJson<DeleteAudioBatchResult>(`/audio-batches/${batchId}`, 'DELETE', opts)
}

/** Purge one item's audio from R2 but keep its transcript (privacy option). */
export async function purgeItemAudio(
  batchId: number,
  itemId: number,
  opts: RequestOptions = {}
): Promise<{ audioPurged: true }> {
  return requestJson<{ audioPurged: true }>(
    `/audio-batches/${batchId}/items/${itemId}/audio`,
    'DELETE',
    opts
  )
}

/** Named alias of the contract's `AudioBatchVideoAbandonResponse`. */
export type AbandonItemVideoResult = AudioBatchVideoAbandonResponse

/** Permanently detach the editor from the source video before timeline edits. */
export async function abandonItemVideo(
  batchId: number,
  itemId: number,
  opts: RequestOptions = {}
): Promise<AbandonItemVideoResult> {
  return requestJson<AbandonItemVideoResult>(
    `/audio-batches/${batchId}/items/${itemId}/video`,
    'DELETE',
    opts
  )
}

/**
 * Delete ONE transcription (file), or cancel it when a worker is still processing
 * the row. The backend also drops the parent batch when this was its last item.
 */
export async function deleteAudioItem(
  batchId: number,
  itemId: number,
  opts: RequestOptions = {}
): Promise<DeleteAudioItemResult> {
  return requestJson<DeleteAudioItemResult>(
    `/audio-batches/${batchId}/items/${itemId}`,
    'DELETE',
    opts
  )
}

/** Estimates affordability without reserving credits; the worker gate remains authoritative. */
export async function preflightAudioBatch(
  input: AudioBatchPreflightRequest,
  opts: RequestOptions & { signal?: AbortSignal } = {}
): Promise<AudioBatchPreflightResponse> {
  try {
    return await client(opts).request<AudioBatchPreflightResponse>(
      '/api/v1/audio-batches/preflight',
      { method: 'POST', body: JSON.stringify(input), signal: opts.signal }
    )
  } catch (error) {
    throw mapBatchError(error, (status) => `Preflight failed (HTTP ${status}).`)
  }
}

/**
 * Re-transcribe a failed item. Resolves once re-enqueued. On 409 the thrown
 * `AudioBatchApiError.code` distinguishes `audio_unavailable` (audio gone — the
 * user must re-upload) from `not_failed`.
 */
export async function retryAudioItem(
  batchId: number,
  itemId: number,
  opts: RequestOptions = {}
): Promise<{ status: string }> {
  try {
    return await client(opts).request<{ status: string }>(
      `/api/v1/audio-batches/${batchId}/items/${itemId}/retry`,
      { method: 'POST' }
    )
  } catch (error) {
    throw mapBatchError(error, (status) => `Retry failed (HTTP ${status}).`)
  }
}

// ---------------------------------------------------------------------------
// Audio editing — 補句 TTS / 重新配音 / 刪除 / 匯出片段
// ---------------------------------------------------------------------------

/** Voice selector for TTS synthesis. PR1 supports cloning an in-transcript speaker. */
export type VoiceSelector = { type: 'clone'; speaker: string }

/** `POST segments/tts-preview` result — an auditionable clip, nothing persisted yet. */
export interface TtsPreviewResult {
  previewId: string
  audioUrl: string
  durationSec: number
}

/**
 * Result of a committed audio edit (insert / revoice / delete): the item's FULL
 * updated segment list + the new audio pointer + version. `focusId` is the cue
 * the editor should select afterwards (the new cue on insert, the target on revoice).
 */
export interface AudioEditResult {
  segments: TranscriptSegmentView[]
  audioUrl: string | null
  audioExpired: boolean
  audioEditSeq: number
  durationSec: number | null
  waveformPeaks: number[] | null
  focusId?: number
}

/** Generate a TTS preview clip in the target speaker's cloned voice. */
export async function ttsPreview(
  batchId: number,
  itemId: number,
  params: { text: string; voice: VoiceSelector; speed?: number },
  opts: RequestOptions = {}
): Promise<TtsPreviewResult> {
  return requestJson<TtsPreviewResult>(
    `/audio-batches/${batchId}/items/${itemId}/segments/tts-preview`,
    'POST',
    opts,
    params
  )
}

/** Commit an inserted AI cue at `insertAtSec` from a previously generated preview. */
export async function insertSegment(
  batchId: number,
  itemId: number,
  params: {
    previewId: string
    insertAtSec: number
    text: string
    voice: VoiceSelector
    expectedSeq?: number
  },
  opts: RequestOptions = {}
): Promise<AudioEditResult> {
  return requestJson<AudioEditResult>(
    `/audio-batches/${batchId}/items/${itemId}/segments/insert`,
    'POST',
    opts,
    params
  )
}

/** Re-voice one cue's audio from a previously generated preview. */
export async function revoiceSegment(
  batchId: number,
  itemId: number,
  segmentId: number,
  params: { previewId: string; text: string; voice: VoiceSelector; expectedSeq?: number },
  opts: RequestOptions = {}
): Promise<AudioEditResult> {
  return requestJson<AudioEditResult>(
    `/audio-batches/${batchId}/items/${itemId}/segments/${segmentId}/revoice`,
    'POST',
    opts,
    params
  )
}

/** Delete one or many cues (single delete = one id) and re-cut the audio. */
export async function deleteSegments(
  batchId: number,
  itemId: number,
  ids: number[],
  expectedSeq: number | undefined,
  opts: RequestOptions = {}
): Promise<AudioEditResult> {
  return requestJson<AudioEditResult>(
    `/audio-batches/${batchId}/items/${itemId}/segments`,
    'DELETE',
    opts,
    { ids, expectedSeq }
  )
}

/** Export a contiguous run of cues as a single WAV blob (non-destructive). */
export async function exportSegmentsAudio(
  batchId: number,
  itemId: number,
  ids: number[],
  opts: RequestOptions = {}
): Promise<Blob> {
  const path = `/api/v1/audio-batches/${batchId}/items/${itemId}/segments/export?ids=${ids.join(',')}`
  try {
    return await client(opts).requestBlob(path, { method: 'GET' })
  } catch (error) {
    throw mapBatchError(error, (status) => `Export failed (HTTP ${status}).`)
  }
}

/** Download the item's whole current audio (edited ?? master) as a Blob. */
export async function downloadItemAudio(
  batchId: number,
  itemId: number,
  opts: RequestOptions = {}
): Promise<Blob> {
  const path = `/api/v1/audio-batches/${batchId}/items/${itemId}/audio/download`
  try {
    return await client(opts).requestBlob(path, { method: 'GET' })
  } catch (error) {
    throw mapBatchError(error, (status) => `Audio download failed (HTTP ${status}).`)
  }
}
